import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';

class DashboardAuthService {
  constructor() {
    this.apiClient = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include auth token
    this.apiClient.interceptors.request.use(
      (config) => {
        const token = this.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.apiClient.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid, logout user
          this.logout();
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Login to dashboard
   * @param {string} username 
   * @param {string} password 
   * @returns {Promise<Object>} Login response
   */
  async login(username, password) {
    try {
      const response = await this.apiClient.post('/dashboard/login', {
        username,
        password,
      });

      if (response.data.success && response.data.token) {
        // Store token and user info
        localStorage.setItem('dashboardToken', response.data.token);
        localStorage.setItem('dashboardUser', JSON.stringify(response.data.user));
        
        console.log('Dashboard login successful');
        return response.data;
      } else {
        throw new Error(response.data.message || 'Login failed');
      }
    } catch (error) {
      console.error('Dashboard login error:', error);
      const message = error.response?.data?.message || error.message || 'Login failed';
      throw new Error(message);
    }
  }

  /**
   * Verify current token
   * @returns {Promise<boolean>} Token validity
   */
  async verifyToken() {
    try {
      const token = this.getToken();
      if (!token) {
        return false;
      }

      const response = await this.apiClient.get('/dashboard/verify');
      return response.data.success;
    } catch (error) {
      console.error('Token verification failed:', error);
      this.logout(); // Clear invalid token
      return false;
    }
  }

  /**
   * Logout from dashboard
   */
  logout() {
    try {
      // Call logout endpoint (optional, since we're using JWT)
      this.apiClient.post('/dashboard/logout').catch(() => {
        // Ignore errors on logout endpoint
      });
    } catch (error) {
      // Ignore errors
    } finally {
      // Always clear local storage
      localStorage.removeItem('dashboardToken');
      localStorage.removeItem('dashboardUser');
      console.log('Dashboard logout successful');
    }
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} Authentication status
   */
  isAuthenticated() {
    const token = this.getToken();
    const user = this.getCurrentUser();
    return !!(token && user);
  }

  /**
   * Get stored auth token
   * @returns {string|null} Auth token
   */
  getToken() {
    return localStorage.getItem('dashboardToken');
  }

  /**
   * Get current user info
   * @returns {Object|null} User object
   */
  getCurrentUser() {
    const userStr = localStorage.getItem('dashboardUser');
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch (error) {
        console.error('Error parsing user data:', error);
        return null;
      }
    }
    return null;
  }

  /**
   * Check authentication status and verify token
   * @returns {Promise<boolean>} Authentication status
   */
  async checkAuth() {
    if (!this.isAuthenticated()) {
      return false;
    }

    // Verify token with server
    return await this.verifyToken();
  }
}

export default new DashboardAuthService();
